import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Linkedin, Copy, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { shortLabelFor, scoreColor, roundScore, levelColor } from "@/lib/categoryChip";

export default function Badge() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [config, setConfig] = useState({ public_app_url: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([
          api.get(`/report/${id}`),
          api.get(`/config`).catch(() => ({ data: {} })),
        ]);
        setReport(r.data);
        setConfig((prev) => ({ ...prev, ...(c.data || {}) }));
      } catch (e) {
        toast.error("Badge not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C6F84C]" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Not found</div>
            <h1 className="mt-3 text-3xl font-bold">This badge doesn't exist.</h1>
            <Link to="/quiz" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#C6F84C] text-black font-bold px-6 py-3">
              Take the audit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isBestInClass = report.level === "Best-in-Class";
  const base = config.public_app_url || (typeof window !== "undefined" ? window.location.origin : "");
  const badgeUrl = `${base}/badge/${id}`;
  const shareUrl = `${base}/api/share/badge/${id}`;
  const linkedInHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(badgeUrl);
      toast.success("Link copied.");
    } catch (e) {
      toast.error("Couldn't copy.");
    }
  };

  return (
    <div className="grain min-h-screen">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Big shareable badge card */}
        <div
          className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-black to-[#0E1512] p-10 md:p-14 overflow-hidden"
          data-testid="public-badge-card"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C6F84C]/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C6F84C] font-bold">
              <ShieldCheck className="w-4 h-4" /> Verified · QR Readiness Audit
            </div>

            <div className="mt-8 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
                  {isBestInClass ? "Best-in-Class certified" : `Level: ${report.level}`}
                </div>
                <div className="mt-3 flex items-baseline gap-3">
                  <div
                    className="font-mono font-black text-8xl md:text-9xl leading-none tabular-nums lime-text-glow"
                    style={{ color: levelColor(report.level) }}
                    data-testid="badge-score-value"
                  >
                    {report.overall}
                  </div>
                  <div className="text-zinc-500 text-2xl">/100</div>
                </div>
                <div className="mt-4 text-zinc-400 max-w-md">
                  Scored against the six-category QR Readiness rubric —
                  design, placement, CTA, tracking, mobile UX and security.
                </div>
              </div>

              {isBestInClass && (
                <div className="rounded-2xl border border-emerald-400/40 bg-emerald-400/5 px-5 py-4 text-center">
                  <Sparkles className="w-5 h-5 text-emerald-400 mx-auto" />
                  <div className="mt-2 text-xs uppercase tracking-widest text-emerald-400 font-bold">
                    Top 5%
                  </div>
                  <div className="mt-1 text-white font-bold">Best-in-Class</div>
                </div>
              )}
            </div>

            {/* Category mini-strip */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(report.categories).map(([key, score]) => (
                <div key={key} className="rounded-lg border border-white/10 bg-black/40 p-3" data-testid={`badge-chip-${key}`}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {shortLabelFor(key, report.category_labels)}
                  </div>
                  <div className="mt-1 font-mono font-bold" style={{ color: scoreColor(score) }}>
                    {roundScore(score)}
                  </div>
                </div>
              ))}
            </div>

            {/* Share row */}
            <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-3 items-center">
              <a
                href={linkedInHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-badge-linkedin-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] text-black font-bold px-5 py-3 transition-colors duration-200"
              >
                <Linkedin className="w-4 h-4" /> Share on LinkedIn
              </a>
              <button
                type="button"
                onClick={copyLink}
                data-testid="public-badge-copy-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/40 text-white font-medium px-5 py-3 transition-colors duration-200"
              >
                <Copy className="w-4 h-4" /> Copy link
              </button>
              <Link
                to="/methodology"
                className="ml-auto text-sm text-zinc-400 hover:text-white transition-colors duration-200"
                data-testid="public-badge-methodology-link"
              >
                See rubric →
              </Link>
            </div>
          </div>
        </div>

        {/* Take-the-audit CTA */}
        <div className="mt-10 rounded-2xl border border-white/10 bg-[#121212] p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Want your own score?</div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">Take the free 3-minute audit.</h2>
          </div>
          <Link
            to="/quiz"
            data-testid="badge-take-audit-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] text-black font-bold px-6 py-3.5 transition-colors duration-200"
          >
            Start free audit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
